import * as Sentry from '@sentry/react';

/**
 * Configuração do Sentry para monitoramento de erros
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Sentry] DSN não configurado. Monitoramento de erros desabilitado.'
    );
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      // Captura erros do React
      Sentry.browserTracingIntegration(),
      // Captura replay de sessão para debug
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Configurações adicionais
    beforeSend(event) {
      // Filtrar erros conhecidos que não são críticos
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes('ResizeObserver loop limit exceeded')) {
          return null; // Não enviar este erro comum do browser
        }
      }

      return event;
    },

    // Tags globais
    initialScope: {
      tags: {
        component: 'checkout-frontend',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      },
    },
  });

  // Configurar usuário se disponível
  Sentry.setUser({
    id: 'anonymous',
    ip_address: '{{auto}}',
  });
};

/**
 * Captura erro customizado com contexto adicional
 */
export const captureException = (
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: 'error' | 'warning' | 'info';
  }
) => {
  Sentry.withScope(scope => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      scope.setContext('additional_info', context.extra);
    }

    if (context?.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
};

/**
 * Captura mensagem customizada
 */
export const captureMessage = (
  message: string,
  level: 'error' | 'warning' | 'info' = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) => {
  Sentry.withScope(scope => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      scope.setContext('additional_info', context.extra);
    }

    scope.setLevel(level);
    Sentry.captureMessage(message);
  });
};

/**
 * Adiciona breadcrumb personalizado
 */
export const addBreadcrumb = (
  message: string,
  category: string = 'custom',
  level: 'error' | 'warning' | 'info' | 'debug' = 'info',
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Define informações do usuário no Sentry para melhor rastreamento e suporte
 *
 * @param userInfo - Informações do usuário
 */
export const setSentryUser = (userInfo: {
  id?: string;
  email?: string;
  username?: string;
  name?: string;
  phone?: string;
  segment?: string;
  additionalData?: Record<string, unknown>;
}) => {
  Sentry.setUser({
    id: userInfo.id || userInfo.email || 'anonymous',
    email: userInfo.email,
    username: userInfo.username || userInfo.name,
    segment: userInfo.segment || 'checkout-user',
    // Dados adicionais do usuário
    phone: userInfo.phone,
    name: userInfo.name,
    ...userInfo.additionalData,
  });

  // Adicionar breadcrumb para rastrear quando o usuário foi identificado
  addBreadcrumb(
    `Usuário identificado: ${userInfo.name || userInfo.email || 'Anônimo'}`,
    'user',
    'info',
    {
      hasEmail: !!userInfo.email,
      hasPhone: !!userInfo.phone,
      hasName: !!userInfo.name,
    }
  );
};

/**
 * Limpa informações do usuário do Sentry
 * Útil em logout ou quando o usuário muda
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);

  addBreadcrumb('Usuário deslogado/limpo', 'user', 'info');
};

/**
 * Adiciona contexto adicional sobre o usuário sem substituir dados existentes
 * Útil para adicionar informações progressivamente conforme elas ficam disponíveis
 */
export const updateSentryUserContext = (context: Record<string, unknown>) => {
  Sentry.setContext('user_context', context);
};

export { Sentry };
